import type { ApiExtensionRequestResponseType } from "@/types/entities/extension";
import axios from "@/utils/axios";
import { useMutation, useQuery } from "@tanstack/react-query";

interface GetParams {
  status?: "PENDING" | "APPROVED" | "DENIED" | "SCALED";
  scaled_to?: string;
}

export const useGetExtensionRequests = (
  formality_id: string,
  params?: GetParams,
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: ["extension-requests", formality_id, params],
    queryFn: async (): Promise<ApiExtensionRequestResponseType> => {
      const response = await axios.get(
        "/admin/v1/node-time-extension-requests",
        {
          params: {
            filter: {
              formality_request_id: formality_id,
              ...(params?.status && { status: params.status }),
              ...(params?.scaled_to && { scaled_to: params.scaled_to }),
            },
            relationships: [
              "nodeTimeExtension",
              "nodeTimeExtension.nodeExpiration.node.executions",
              "formalityRequest",
              "formalityRequest.applicant",
              "formalityRequest.flowExecution",
              "formalityRequest.flowExecution.activeNodeExecution",
              "formalityRequest.flowExecution.activeNodeExecution.node",
              "approvedOrDeniedBy",
              "scaledBy",
              "scaledTo",
              "files",
              "comments",
              "comments.creator",
              "comments.creatorApplicant",
            ].join(","),
          },
        },
      );
      return response.data;
    },
    enabled: !!formality_id && enabled,
  });
};

export const useUpdateExtensionRequest = (id: string) => {
  return useMutation({
    mutationFn: async (data: {
      action: "APPROVE" | "DENY" | "SCALE";
      comment?: string;
      scaled_to?: string;
    }) => {
      const response = await axios.patch(
        `/admin/v1/node-time-extension-requests/${id}`,
        {
          action: data.action,
          ...(data.action === "DENY" && data.comment
            ? { comment: data.comment }
            : {}),
          ...(data.action === "SCALE" && data.comment
            ? { comment: data.comment }
            : {}),
          ...(data.action === "SCALE" && data.scaled_to
            ? { scaled_to: data.scaled_to }
            : {}),
        },
      );
      return response.data;
    },
  });
};
